/**
 * Persian labels, one map per enum.
 *
 * `labelFa` is what goes on screen. `labelFaDisplay`, where present, is the
 * longer form used on a detail page or in a document, and `tone` is the badge
 * variant so the mapping from status to colour is also in one place.
 *
 * A status speaks in three tones and no more: `success` is done, `warning`
 * waits on someone, `danger` is a problem. Every other status is `neutral`,
 * so a table of ten rows shows at most three colours and each one means
 * something. Grade is not a status and keeps its own tone.
 */

import {
  ActingScope,
  ActivityKind,
  AdStatus,
  BidStatus,
  TicketCategory,
  TicketStatus,
  BlockReason,
  BrandStatus,
  ClaimCategory,
  ClaimStage,
  DealSide,
  DealState,
  DocumentCategory,
  DocumentOrigin,
  DomesticDeliveryLocation,
  Grade,
  Incoterm,
  InvoiceStatus,
  KycStatus,
  LedgerEntryKind,
  MandateStatus,
  MatchOrigin,
  MetalKey,
  NegotiationParam,
  NegotiationStage,
  NewsStatus,
  NotificationChannel,
  NotificationTopic,
  OfferKind,
  OfferStatus,
  ParamStatus,
  PartyKind,
  PartyStatus,
  PaymentMethod,
  PriceSourceKind,
  SaleMethod,
  Side,
  SkipReason,
  TradingRole,
  WeightBasis,
  WeightTolerance,
} from './enums'

export type BadgeTone =
  | 'neutral'
  | 'accent'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'premium'

export interface Label {
  labelFa: string
  labelFaDisplay?: string
  tone?: BadgeTone
}

type LabelMap<T extends string> = Record<T, Label>

/** The eight offer statuses plus REJECTED. No synonyms exist. */
export const OFFER_STATUS: LabelMap<OfferStatus> = {
  DRAFT: { labelFa: 'پیش‌نویس', tone: 'neutral' },
  PENDING_APPROVAL: { labelFa: 'در انتظار تأیید', tone: 'warning' },
  ACTIVE: { labelFa: 'فعال', tone: 'success' },
  SUSPENDED: { labelFa: 'متوقف', tone: 'warning' },
  PARTIALLY_FILLED: { labelFa: 'بخشی انجام شده', tone: 'neutral' },
  FILLED: { labelFa: 'انجام شده', tone: 'success' },
  EXPIRED: { labelFa: 'منقضی شده', tone: 'neutral' },
  CANCELLED: { labelFa: 'لغو شده', tone: 'danger' },
  REJECTED: { labelFa: 'رد شده', tone: 'danger' },
}

/** Slug used in a URL or a filter chip. */
export const OFFER_STATUS_SLUG: Record<OfferStatus, string> = {
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pending',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  PARTIALLY_FILLED: 'partial',
  FILLED: 'filled',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  REJECTED: 'rejected',
}

export const DEAL_STATE: LabelMap<DealState> = {
  MATCHED: { labelFa: 'تطبیق شده', tone: 'neutral' },
  AWAITING_COMMISSION: { labelFa: 'در انتظار پرداخت کارمزد', tone: 'warning' },
  IDENTITIES_REVEALED: { labelFa: 'معرفی طرفین', tone: 'neutral' },
  AWAITING_SETTLEMENT: { labelFa: 'در انتظار تسویه', tone: 'warning' },
  SETTLED: { labelFa: 'تسویه شده', tone: 'success' },
  SHIPPED: { labelFa: 'ارسال شده', tone: 'neutral' },
  /** A weight or quality discrepancy is its own chip, never part of this name. */
  DELIVERED: { labelFa: 'تحویل شده', tone: 'success' },
  CLOSED: { labelFa: 'بسته شده', tone: 'success' },
  IN_CLAIM: { labelFa: 'شکایت باز', tone: 'danger' },
  CANCELLED: { labelFa: 'لغو شده', tone: 'danger' },
}

/** The deal room rail, in order. */
export const DEAL_MILESTONE_LABEL: Record<string, string> = {
  MATCHED: 'تطبیق',
  AWAITING_COMMISSION: 'پرداخت کارمزد',
  IDENTITIES_REVEALED: 'معرفی طرفین',
  PROFORMA_ISSUED: 'صدور پیش‌فاکتور کالا',
  GOODS_SETTLED: 'تسویه نهایی',
  SHIPPED: 'ارسال',
  DELIVERED: 'تحویل',
  CLOSED: 'بسته‌شدن',
}

export const NEGOTIATION_STAGE: LabelMap<NegotiationStage> = {
  NEW_FILE: { labelFa: 'پرونده جدید' },
  QUALIFIED: { labelFa: 'واجد شرایط' },
  SCHEDULED: { labelFa: 'زمان‌بندی شده' },
  CONTACTED: { labelFa: 'تماس گرفته شده' },
  NEGOTIATION_OPEN: { labelFa: 'شروع مذاکره' },
  OFFER_PRESENTED: { labelFa: 'پیشنهاد ارائه شده' },
  OFFER_CONFIRMED: { labelFa: 'پیشنهاد تأیید شده' },
  AGREED: { labelFa: 'توافق شد' },
}

export const NEGOTIATION_PARAM: LabelMap<NegotiationParam> = {
  PRODUCT: { labelFa: 'کالا' },
  QUALITY_GRADE: { labelFa: 'درجه کیفیت' },
  QUANTITY: { labelFa: 'مقدار' },
  PRICE: { labelFa: 'قیمت' },
  DELIVERY_PLACE: { labelFa: 'محل تحویل' },
  DELIVERY_TIME: { labelFa: 'زمان تحویل' },
  PAYMENT_TERMS: { labelFa: 'شرایط پرداخت' },
  VALIDITY: { labelFa: 'اعتبار' },
}

export const PARAM_STATUS: LabelMap<ParamStatus> = {
  AGREED: { labelFa: 'توافق شده', tone: 'success' },
  OPEN: { labelFa: 'باز', tone: 'neutral' },
  DIFFERENT: { labelFa: 'اختلاف', tone: 'warning' },
}

export const GRADE: LabelMap<Grade> = {
  GOLD: { labelFa: 'طلایی', tone: 'premium' },
  SILVER: { labelFa: 'نقره‌ای', tone: 'neutral' },
  BRONZE: { labelFa: 'برنزی', tone: 'accent' },
  NONE: { labelFa: 'بدون گرید', labelFaDisplay: 'بدون گرید (کاربر عادی)', tone: 'neutral' },
}

export const PARTY_KIND: LabelMap<PartyKind> = {
  PERSON: { labelFa: 'شخص حقیقی' },
  COMPANY: { labelFa: 'شخص حقوقی' },
}

export const PARTY_STATUS: LabelMap<PartyStatus> = {
  VISITOR: { labelFa: 'بازدیدکننده', tone: 'neutral' },
  BASIC: { labelFa: 'کاربر پایه', tone: 'neutral' },
  PROFESSIONAL: { labelFa: 'کاربر حرفه‌ای', tone: 'success' },
  SUSPENDED: { labelFa: 'تعلیق شده', tone: 'warning' },
  REJECTED: { labelFa: 'رد شده', tone: 'danger' },
}

export const ACTIVITY_KIND: LabelMap<ActivityKind> = {
  PRODUCER: { labelFa: 'تولیدکننده' },
  CONSUMER: { labelFa: 'مصرف‌کننده' },
  TRADER: { labelFa: 'بازرگان' },
}

export const TRADING_ROLE: LabelMap<TradingRole> = {
  BUYER: { labelFa: 'خریدار' },
  SELLER: { labelFa: 'فروشنده' },
  BOTH: { labelFa: 'هر دو' },
}

export const ACTING_SCOPE: LabelMap<ActingScope> = {
  SELF: { labelFa: 'به نام خودم' },
  OTHERS: { labelFa: 'به نام دیگران' },
  BOTH: { labelFa: 'هر دو' },
}

export const BLOCK_REASON: LabelMap<BlockReason> = {
  NON_DELIVERY: { labelFa: 'عدم تحویل کالا' },
  NON_PAYMENT: { labelFa: 'عدم پرداخت مبلغ کالا' },
  UNPAID_COMMISSION: { labelFa: 'عدم پرداخت کارمزد' },
  FIVE_CLAIMS: { labelFa: 'رسیدن به ۵ شکایت' },
  OFF_PLATFORM_DEALING: { labelFa: 'معامله خارج از پلتفرم' },
}

export const OFFER_KIND: LabelMap<OfferKind> = {
  DOMESTIC: { labelFa: 'داخلی' },
  EXPORT: { labelFa: 'صادراتی' },
}

export const SALE_METHOD: LabelMap<SaleMethod> = {
  OPEN_AUCTION: { labelFa: 'حراج باز' },
  SEALED_AUCTION: { labelFa: 'حراج بسته' },
  TENDER: { labelFa: 'مزایده' },
}

export const SIDE: LabelMap<Side> = {
  OFFER: { labelFa: 'عرضه' },
  RFQ: { labelFa: 'تقاضا' },
}

export const DEAL_SIDE: LabelMap<DealSide> = {
  SELLER: { labelFa: 'فروشنده' },
  BUYER: { labelFa: 'خریدار' },
}

/** Four codes, labelled per side, because پیش فروش is پیش خرید to a buyer. */
export const PAYMENT_METHOD_SELL: LabelMap<PaymentMethod> = {
  CASH: { labelFa: 'نقدی' },
  CREDIT_CASH: { labelFa: 'نقدی‌اعتباری' },
  CREDIT: { labelFa: 'اعتباری' },
  PRE_TRADE: { labelFa: 'پیش‌فروش' },
}

export const PAYMENT_METHOD_BUY: LabelMap<PaymentMethod> = {
  CASH: { labelFa: 'نقدی' },
  CREDIT_CASH: { labelFa: 'نقدی‌اعتباری' },
  CREDIT: { labelFa: 'اعتباری' },
  PRE_TRADE: { labelFa: 'پیش‌خرید' },
}

export const WEIGHT_TOLERANCE: LabelMap<WeightTolerance> = {
  UNDER_5: { labelFa: 'زیر ۵ درصد' },
  EXACT_5: { labelFa: '۵ درصد' },
  FIVE_TO_TEN: { labelFa: '۵ تا ۱۰ درصد' },
}

export const WEIGHT_BASIS: LabelMap<WeightBasis> = {
  ORIGIN_SCALE: { labelFa: 'باسکول مبدا' },
  DEST_SCALE: { labelFa: 'باسکول مقصد' },
  THIRD_PARTY_SCALE: { labelFa: 'باسکول شخص ثالث' },
  ORIGIN_CUSTOMS: { labelFa: 'گمرک مبدا' },
  DEST_CUSTOMS: { labelFa: 'گمرک مقصد' },
}

export const INCOTERM: LabelMap<Incoterm> = {
  EXW: { labelFa: 'EXW (تحویل کالا در نقطه عزیمت در مبدا)' },
  FCA: { labelFa: 'FCA (تحویل کالا به حمل‌کننده در مبدا روی وسیله حمل)' },
  CPT: { labelFa: 'CPT (تحویل با پرداخت کرایه حمل تا مقصد به غیر از دریایی)' },
  CIP: { labelFa: 'CIP (تحویل با پرداخت کرایه حمل و بیمه تا مقصد به غیر از دریایی)' },
  DAP: { labelFa: 'DAP (تحویل در محل معین در مقصد)' },
  DPU: { labelFa: 'DPU (تحویل در محل معین در مقصد به صورت تخلیه شده)' },
  DDP: { labelFa: 'DDP (تحویل کالا در مقصد با پرداخت عوارض گمرکی واردات)' },
  FAS: { labelFa: 'FAS (تحویل کالا در کنار کشتی در مبدا)' },
  FOB: { labelFa: 'FOB (تحویل کالا در عرشه کشتی در مبدا)' },
  CFR: { labelFa: 'CFR (تحویل با پرداخت کرایه حمل تا مقصد به صورت دریایی)' },
  CIF: { labelFa: 'CIF (تحویل با پرداخت کرایه حمل و بیمه تا مقصد به صورت دریایی)' },
}

export const DOMESTIC_DELIVERY_LOCATION: LabelMap<DomesticDeliveryLocation> = {
  SELLER_WAREHOUSE: { labelFa: 'انبار فروشنده' },
  BUYER_WAREHOUSE: { labelFa: 'انبار خریدار' },
  SELLER_FACTORY: { labelFa: 'کارخانه فروشنده' },
  BUYER_FACTORY: { labelFa: 'کارخانه خریدار' },
  NAMED_PLACE: { labelFa: 'محل توافقی' },
  CARRIER: { labelFa: 'تحویل به حمل‌کننده' },
}

export const TICKET_CATEGORY: LabelMap<TicketCategory> = {
  PROFILE_CORRECTION: { labelFa: 'اصلاح اطلاعات حساب' },
  DOCUMENTS: { labelFa: 'مدارک' },
  TRADING: { labelFa: 'عرضه، تقاضا و معامله' },
  OTHER: { labelFa: 'سایر' },
  DEAL_FOLLOW_UP: { labelFa: 'پیگیری معامله' },
  ANCILLARY: { labelFa: 'خدمات جانبی' },
}

export const TICKET_STATUS: LabelMap<TicketStatus> = {
  OPEN: { labelFa: 'در انتظار پاسخ', tone: 'warning' },
  ANSWERED: { labelFa: 'پاسخ داده شد', tone: 'success' },
  CLOSED: { labelFa: 'بسته شده', tone: 'neutral' },
}

export const BID_STATUS: LabelMap<BidStatus> = {
  ACTIVE: { labelFa: 'فعال', tone: 'success' },
  WON: { labelFa: 'معامله شد', tone: 'success' },
  NOT_MATCHED: { labelFa: 'معامله نشد', tone: 'neutral' },
  WITHDRAWN: { labelFa: 'پس گرفته شد', tone: 'neutral' },
  // Out of the competition reads red wherever it is drawn (2026-10-27).
  SKIPPED: { labelFa: 'خارج از رقابت', tone: 'danger' },
  EXPIRED: { labelFa: 'منقضی شده', tone: 'neutral' },
}

/**
 * Why a bid or a response was passed over. The map serves both sides: a bid
 * on an offer and a response on a demand, so no reason names one side's
 * listing («موجودی عرضه» would be wrong under a demand).
 */
export const SKIP_REASON: LabelMap<SkipReason> = {
  LARGER_THAN_REMAINDER: { labelFa: 'بیشتر از مقدار مانده' },
  BELOW_MIN_LOT: { labelFa: 'کمتر از حداقل خرید' },
  BELOW_BASE_PRICE: { labelFa: 'کمتر از قیمت پایه' },
  BIDDER_INELIGIBLE: { labelFa: 'پیشنهاددهنده مجاز نیست' },
  OUTSIDE_WINDOW: { labelFa: 'خارج از ساعت معاملات' },
}

export const KYC_STATUS: LabelMap<KycStatus> = {
  NOT_STARTED: { labelFa: 'شروع نشده', tone: 'neutral' },
  MOBILE_PENDING: { labelFa: 'در انتظار تأیید موبایل', tone: 'warning' },
  MOBILE_VERIFIED: { labelFa: 'موبایل تأیید شد', tone: 'neutral' },
  BASE_CREATED: { labelFa: 'حساب پایه', tone: 'neutral' },
  BUSINESS_PENDING: { labelFa: 'اطلاعات تجاری ثبت نشده', tone: 'warning' },
  DOCUMENTS_PENDING: { labelFa: 'مدارک بارگذاری نشده', tone: 'warning' },
  UNDER_REVIEW: { labelFa: 'در صف بررسی', tone: 'warning' },
  INQUIRY_FAILED: { labelFa: 'استعلام ناموفق', tone: 'danger' },
  TERMS_PENDING: { labelFa: 'قرارداد امضا نشده', tone: 'warning' },
  GRADING_PENDING: { labelFa: 'در انتظار گریدبندی', tone: 'warning' },
  ACTIVE: { labelFa: 'فعال برای معامله', tone: 'success' },
  REJECTED: { labelFa: 'رد شده', tone: 'danger' },
}

export const DOCUMENT_CATEGORY: LabelMap<DocumentCategory> = {
  KYC: { labelFa: 'احراز هویت' },
  LISTING: { labelFa: 'عرضه' },
  DEAL: { labelFa: 'معامله' },
  NOTICE: { labelFa: 'اعلامیه' },
  WAYBILL: { labelFa: 'بارنامه' },
  PACKING: { labelFa: 'پکینگ‌لیست' },
  INSURANCE: { labelFa: 'بیمه‌نامه' },
  ASSAY: { labelFa: 'آنالیز' },
  INVOICE: { labelFa: 'صورتحساب' },
  WEIGHBRIDGE: { labelFa: 'قبض باسکول' },
  OTHER: { labelFa: 'سایر' },
}

/** Where a deal document came from. Also the two tabs of the documents page, beside «همه». */
export const DOCUMENT_ORIGIN: LabelMap<DocumentOrigin> = {
  PLATFORM: { labelFa: 'از پلتفرم' },
  PARTY: { labelFa: 'از طرفین' },
}

/**
 * One shape, short nouns. `AUTO_REJECTED` reads as a closed case: why the
 * gate closed it is the case's reason, shown beside the stage, never part of
 * the stage name.
 */
export const CLAIM_STAGE: LabelMap<ClaimStage> = {
  AUTO_REJECTED: { labelFa: 'بسته شده', tone: 'success' },
  SUBMITTED: { labelFa: 'ثبت شکایت', tone: 'neutral' },
  UNDER_REVIEW: { labelFa: 'بررسی', tone: 'warning' },
  EVIDENCE_REQUESTED: { labelFa: 'جمع‌آوری مدارک', tone: 'warning' },
  COUNTERPARTY_NOTIFIED: { labelFa: 'پاسخ طرف مقابل', tone: 'warning' },
  EXPERT_OPINION: { labelFa: 'کارشناسی', tone: 'neutral' },
  NEGOTIATION: { labelFa: 'مذاکره', tone: 'neutral' },
  AGREEMENT: { labelFa: 'توافق', tone: 'neutral' },
  DECIDED: { labelFa: 'تعیین نتیجه', tone: 'neutral' },
  UPHELD: { labelFa: 'پذیرفته شده', tone: 'success' },
  DISMISSED: { labelFa: 'رد شده', tone: 'danger' },
  CLOSED: { labelFa: 'بسته شده', tone: 'success' },
}

/** The seven headings of the claim form, as the form writes them. */
export const CLAIM_CATEGORY: LabelMap<ClaimCategory> = {
  QUANTITY: { labelFa: 'کمیت' },
  QUALITY: { labelFa: 'کیفیت' },
  DAMAGE_PACKAGING: { labelFa: 'آسیب‌دیدگی و بسته‌بندی' },
  DELIVERY_TRANSPORT: { labelFa: 'تحویل و حمل و نقل' },
  PAYMENT: { labelFa: 'مالی و پرداختی' },
  WARRANTY_DOCUMENTS: { labelFa: 'گارانتی، خدمات پس از فروش یا مستندات' },
  FRAUD: { labelFa: 'تخلفات یا کلاهبرداری' },
}

export const LEDGER_ENTRY_KIND: LabelMap<LedgerEntryKind> = {
  COMMISSION_CHARGE: { labelFa: 'کارمزد معامله' },
  COMMISSION_VAT: { labelFa: 'ارزش افزوده کارمزد' },
  COMMISSION_PAYMENT: { labelFa: 'پرداخت کارمزد' },
  COMMISSION_REFUND: { labelFa: 'برگشت کارمزد' },
  CANCELLATION_PENALTY: { labelFa: 'جریمه لغو معامله' },
  DAMAGE_COMPENSATION: { labelFa: 'جبران خسارت' },
  ANCILLARY_SERVICE: { labelFa: 'خدمات جانبی' },
  DEPOSIT: { labelFa: 'واریز' },
  WITHDRAWAL: { labelFa: 'برداشت' },
  ADJUSTMENT: { labelFa: 'اصلاح' },
}

export const INVOICE_STATUS: LabelMap<InvoiceStatus> = {
  ISSUED: { labelFa: 'صادر شده', tone: 'warning' },
  PAID: { labelFa: 'پرداخت شده', tone: 'success' },
  OVERDUE: { labelFa: 'سررسید گذشته', tone: 'danger' },
  VOID: { labelFa: 'ابطال شده', tone: 'neutral' },
}

/** One try to pay through the card gateway, from the press to the bank's answer. */
export type PaymentAttemptStatus = 'PENDING' | 'REDIRECTED' | 'SETTLED' | 'FAILED' | 'CANCELLED' | 'EXPIRED'

export const PAYMENT_ATTEMPT_STATUS: LabelMap<PaymentAttemptStatus> = {
  PENDING: { labelFa: 'شروع نشده', tone: 'neutral' },
  REDIRECTED: { labelFa: 'در درگاه', tone: 'warning' },
  SETTLED: { labelFa: 'موفق', tone: 'success' },
  FAILED: { labelFa: 'ناموفق', tone: 'danger' },
  CANCELLED: { labelFa: 'لغو شده', tone: 'neutral' },
  EXPIRED: { labelFa: 'منقضی شده', tone: 'neutral' },
}

export const PRICE_SOURCE_KIND: LabelMap<PriceSourceKind> = {
  MANUAL: { labelFa: 'ثبت دستی' },
  FILE_IMPORT: { labelFa: 'ورود از فایل' },
  CLIENT_FORMULA: { labelFa: 'فرمول شاخص' },
  PLATFORM_MATCH: { labelFa: 'معاملات پلتفرم' },
}

export const NEWS_STATUS: LabelMap<NewsStatus> = {
  FETCHED: { labelFa: 'دریافت شده', tone: 'neutral' },
  SCORED: { labelFa: 'امتیازدهی شده', tone: 'neutral' },
  IN_REVIEW: { labelFa: 'در حال بررسی', tone: 'warning' },
  PUBLISHED: { labelFa: 'منتشر شده', tone: 'success' },
  REJECTED: { labelFa: 'رد شده', tone: 'danger' },
  ARCHIVED: { labelFa: 'بایگانی', tone: 'neutral' },
}

export const AD_STATUS: LabelMap<AdStatus> = {
  SUBMITTED: { labelFa: 'ثبت شده', tone: 'neutral' },
  IN_REVIEW: { labelFa: 'در حال بررسی', tone: 'warning' },
  APPROVED: { labelFa: 'تأیید شده', tone: 'success' },
  REJECTED: { labelFa: 'رد شده', tone: 'danger' },
  EXPIRED: { labelFa: 'منقضی شده', tone: 'neutral' },
}

export const NOTIFICATION_CHANNEL: LabelMap<NotificationChannel> = {
  IN_APP: { labelFa: 'داخل سایت' },
  SMS: { labelFa: 'پیامک' },
  EMAIL: { labelFa: 'ایمیل' },
  PUSH: { labelFa: 'اعلان موبایل' },
}

export const NOTIFICATION_TOPIC: LabelMap<NotificationTopic> = {
  BID: { labelFa: 'پیشنهاد' },
  EXPIRY: { labelFa: 'معتبر تا' },
  MATCH: { labelFa: 'تطبیق' },
  FEE: { labelFa: 'کارمزد' },
  KYC: { labelFa: 'احراز هویت' },
  DELIVERY: { labelFa: 'تحویل' },
  CLAIM: { labelFa: 'شکایت' },
  SYSTEM: { labelFa: 'اطلاعیه' },
}

export const BRAND_STATUS: LabelMap<BrandStatus> = {
  PENDING: { labelFa: 'در انتظار تأیید', tone: 'warning' },
  APPROVED: { labelFa: 'تأیید شده', tone: 'success' },
  REJECTED: { labelFa: 'رد شده', tone: 'danger' },
}

export const MANDATE_STATUS: LabelMap<MandateStatus> = {
  PENDING_CODE: { labelFa: 'در انتظار کد تأیید', tone: 'warning' },
  ACTIVE: { labelFa: 'تأیید شده', tone: 'success' },
  EXPIRED: { labelFa: 'منقضی شده', tone: 'neutral' },
  REVOKED: { labelFa: 'حذف شده', tone: 'neutral' },
}

export const METAL: LabelMap<MetalKey> = {
  COPPER: { labelFa: 'مس' },
  BRASS: { labelFa: 'برنج' },
  ZINC: { labelFa: 'روی' },
  ALU: { labelFa: 'آلومینیوم' },
  LEAD: { labelFa: 'سرب' },
  TIN: { labelFa: 'قلع' },
}

/** Lower case token suffix for the metal swatch, matching the CSS tokens. */
export const METAL_TOKEN: Record<MetalKey, string> = {
  COPPER: 'copper',
  BRASS: 'brass',
  ZINC: 'zinc',
  ALU: 'alu',
  LEAD: 'lead',
  TIN: 'tin',
}

export const MATCH_ORIGIN: LabelMap<MatchOrigin> = {
  ENGINE: { labelFa: 'تطبیق خودکار' },
  OPERATOR: { labelFa: 'تطبیق اپراتور' },
}

/** Grade key in the lower case form the kit's CSS classes use. */
export const GRADE_TOKEN: Record<Grade, 'gold' | 'silver' | 'bronze' | 'none'> = {
  GOLD: 'gold',
  SILVER: 'silver',
  BRONZE: 'bronze',
  NONE: 'none',
}

/** The 31 provinces, for the listing location and the board filter. */
export const PROVINCES = [
  'آذربایجان شرقی',
  'آذربایجان غربی',
  'اردبیل',
  'اصفهان',
  'البرز',
  'ایلام',
  'بوشهر',
  'تهران',
  'چهارمحال و بختیاری',
  'خراسان جنوبی',
  'خراسان رضوی',
  'خراسان شمالی',
  'خوزستان',
  'زنجان',
  'سمنان',
  'سیستان و بلوچستان',
  'فارس',
  'قزوین',
  'قم',
  'کردستان',
  'کرمان',
  'کرمانشاه',
  'کهگیلویه و بویراحمد',
  'گلستان',
  'گیلان',
  'لرستان',
  'مازندران',
  'مرکزی',
  'هرمزگان',
  'همدان',
  'یزد',
] as const
export type Province = (typeof PROVINCES)[number]

/** Read a label, falling back to the code so a missing entry is visible. */
export function labelOf<T extends string>(map: LabelMap<T>, value: T | null | undefined): string {
  if (!value) return '-'
  return map[value]?.labelFa ?? value
}

export function toneOf<T extends string>(map: LabelMap<T>, value: T | null | undefined): BadgeTone {
  if (!value) return 'neutral'
  return map[value]?.tone ?? 'neutral'
}
